import type { HostedEvent } from "@hobbyshopify/backend-client";
import { Rx } from "subtree.js/core";
import { SHOW_MAY_NOTHING, type ShowMay } from "../../core/primitives/hosted-event";
import { type FormStatus, idleForm, idleLoad, type LoadStatus } from "../../core/primitives/load-status";
import type { ShareTarget } from "../../core/primitives/share-links";

/** A show the shop hosts: what it is, its tables and who took them, and what the host may do with it. */
export class ShowState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly event = new Rx<HostedEvent | null>(null);
  readonly may = new Rx<ShowMay>(SHOW_MAY_NOTHING);
  /** A publish or a take-down in flight, and its outcome. */
  readonly publishForm = new Rx<FormStatus>(idleForm);
  /** A publish, a take-down, a cancel or a try of the refunds is in flight: the page does one at a time. */
  readonly acting = new Rx(false);

  /** When the host cancelled the show; nothing about it changes then. */
  readonly cancelledAt = new Rx<Date | null>(null);
  /** Asking to confirm the cancel, and the cancel in flight or what it did. */
  readonly confirmCancel = new Rx(false);
  readonly cancelForm = new Rx<FormStatus>(idleForm);
  /** Whether the cancel is in view: while the show may be called off, and while one is asked, under way or failed. */
  readonly cancelOffered = new Rx(false);
  /** From the tables below: taken tables whose payment stands, or null while they are not read. */
  readonly paidTables = new Rx<number | null>(null);
  /** Trying the refunds the show still owes again. */
  readonly retryForm = new Rx<FormStatus>(idleForm);
  /** A flyer being shrunk and sent, or removed, and how it went. */
  readonly flyerForm = new Rx<FormStatus>(idleForm);
  /** The show's page on hobbyshop.ai, while it is published and going ahead. */
  readonly share = new Rx<ShareTarget | null>(null);
}

export abstract class ShowActions {
  abstract back(): void;
  abstract edit(): void;
  abstract publish(): Promise<void>;
  abstract unpublish(): Promise<void>;
  /** Asks to confirm first. */
  abstract cancelShow(): Promise<void>;
  abstract keepShow(): void;
  abstract retryRefunds(): Promise<void>;
  /** Shrinks the picked image and sets it as the show's flyer, replacing any earlier one. */
  abstract pickFlyer(file: File): Promise<void>;
  abstract removeFlyer(): Promise<void>;
}
